import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Alert,
    Collapse,
    Link,
    useTheme,
    useMediaQuery,
    alpha,
} from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { useClientPortalConfigContext } from "../../context";
import Login from "../Account/LoginDialog";
import Content from "../Layout/Content";
import HomeActionItem from "./HomeActionItem/HomeActionItem";
import { useClientPortalHomeActions } from "./hooks/useClientPortalHomeActions";
import { palette } from "@powerdiary/components-library/lib";
import { useAnalytics } from "../../hooks/useAnalytics";

export default function Home() {
    const { clientPortalConfig } = useClientPortalConfigContext();
    const [expanded, setExpanded] = useState(false);
    const [showReadMore, setShowReadMore] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isDesktopExpanded = !!clientPortalConfig.expandHomeInstructions && !isMobile;
    const { track } = useAnalytics();

    useEffect(() => {
        track("page_view", { page_title: "Home" });
    }, [track]);

    useEffect(() => {
        if (!expanded && contentRef.current) {
            const { scrollHeight, clientHeight } = contentRef.current;
            setShowReadMore(scrollHeight > clientHeight + 1); // +1 for rounding issues
        } else if (expanded) {
            setShowReadMore(true); // Always show when expanded, so user can collapse
        }
    }, [clientPortalConfig.bookingInstructions, expanded]);

    const { actions, showLoginDialog, returnUrl, setShowLoginDialog, shouldDisplayExistingClientsAlert } =
        useClientPortalHomeActions();

    return (
        <Content dataTestIdSuffix="home">
            <Box
                sx={{
                    p: { xxs: 2, sm: 3 },
                    display: "flex",
                    flexDirection: "column",
                    gap: { xxs: 2, sm: 3 },
                }}
            >
                {!clientPortalConfig.brandImageUrl && (
                    <Box
                        sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "160px",
                            borderRadius: "4px",
                            backgroundColor: theme.palette.primary.main,
                            textAlign: "center",
                        }}
                    >
                        <Typography
                            color={palette.neutral["white"]}
                            variant={isMobile ? "paragraphExtraLargeSemiBold" : "heading4"}
                            sx={{
                                textShadow: "0 2px 2px rgba(0, 0, 0, 0.40)",
                            }}
                        >
                            {clientPortalConfig.bannerLine1}
                        </Typography>
                        <Typography
                            variant={isMobile ? "heading3" : "heading1"}
                            color={palette.neutral["white"]}
                            sx={{
                                textShadow: "0 2px 2px rgba(0, 0, 0, 0.40)",
                                fontSize: isMobile ? "28px" : "40px",
                            }}
                        >
                            {clientPortalConfig.bannerLine2}
                        </Typography>
                    </Box>
                )}
                {clientPortalConfig.brandImageUrl && (
                    <Box
                        position="relative"
                        width="100%"
                        display="inline-block"
                        sx={{
                            borderRadius: "4px",
                            overflow: "hidden",
                            ...(isMobile && { height: "160px" }),
                        }}
                    >
                        <Box
                            component="img"
                            data-test-id="image_brading_image"
                            src={clientPortalConfig.brandImageUrl}
                            alt={clientPortalConfig.clientPortalName}
                            sx={{
                                width: "100%",
                                borderRadius: "4px",
                                display: "block",
                                ...(isMobile && {
                                    height: "100%",
                                    objectFit: "cover",
                                    objectPosition: "center",
                                }),
                            }}
                        />

                        <Box
                            data-test-id="section_image_overlay"
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "column",
                                pointerEvents: "none",
                                backgroundColor: clientPortalConfig.useClientPortalBrandingBannerOverlay
                                    ? alpha(theme.palette.primary.main, 0.3)
                                    : "transparent",
                                textAlign: "center",
                            }}
                        >
                            <Typography
                                variant={isMobile ? "paragraphExtraLargeSemiBold" : "heading4"}
                                color={palette.neutral["white"]}
                                sx={{
                                    textShadow: "0 2px 2px rgba(0, 0, 0, 0.40)",
                                }}
                            >
                                {clientPortalConfig.bannerLine1}
                            </Typography>
                            <Typography
                                variant={isMobile ? "heading3" : "heading1"}
                                color={palette.neutral["white"]}
                                data-test-id="text_client_portal_name"
                                sx={{
                                    textShadow: "0 2px 2px rgba(0, 0, 0, 0.40)",
                                    fontSize: isMobile ? "28px" : "40px",
                                }}
                            >
                                {clientPortalConfig.bannerLine2}
                            </Typography>
                        </Box>
                    </Box>
                )}
                {clientPortalConfig.bookingInstructions && (
                    <Card variant="elevation" sx={{ borderRadius: "4px" }}>
                        <CardContent sx={{ display: "flex", flexDirection: "column" }}>
                            <Collapse in={expanded || isDesktopExpanded} collapsedSize={70} timeout={300}>
                                <Box
                                    ref={contentRef}
                                    sx={{
                                        display: "-webkit-box",
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        WebkitLineClamp: expanded || isDesktopExpanded ? "unset" : 3,
                                        whiteSpace: "normal",
                                        mb: expanded || isDesktopExpanded ? 2 : 0,
                                        lineHeight: "18px",
                                    }}
                                >
                                    <Typography
                                        data-test-id="text_booking_instructions"
                                        variant="paragraphSmallRegular"
                                        color="text.secondary"
                                        dangerouslySetInnerHTML={{
                                            __html: clientPortalConfig.bookingInstructions ?? "",
                                        }}
                                    ></Typography>
                                </Box>
                            </Collapse>
                            {showReadMore && !isDesktopExpanded && (
                                <Link
                                    data-test-id="link_read_more"
                                    underline="hover"
                                    color="primary"
                                    variant="small"
                                    width="fit-content"
                                    onClick={() => setExpanded((prev) => !prev)}
                                >
                                    {expanded ? "Read Less" : "Read More"}
                                </Link>
                            )}
                        </CardContent>
                    </Card>
                )}
                {shouldDisplayExistingClientsAlert && (
                    <Alert severity="warning" color="userSelectedColor">
                        This portal is available for existing clients only.
                    </Alert>
                )}
                <Grid container spacing={{ xxs: 2, sm: 3 }}>
                    {actions.map((action, index) => {
                        if (!action.isVisible) {
                            return null;
                        }
                        return (
                            <HomeActionItem
                                key={index}
                                onClick={action.onClick}
                                icon={action.icon}
                                title={action.title}
                                description={action.description}
                            />
                        );
                    })}
                </Grid>
            </Box>
            {showLoginDialog && <Login returnUrl={returnUrl} handleClose={() => setShowLoginDialog(false)} />}
        </Content>
    );
}
